// ─────────────────────────────────────────────────────────────────────────────
// D-3 — Columna FRECUENCIA de la tabla de encuestas.
//
// El cliente aprobó agregarla el 2026-08-02, revirtiendo la decisión anterior de omitirla
// (`CLAUDE.md`: "SurveysTab (Generales) … sin columna Frecuencia").
//
// IMPORTANTE — de dónde sale el dato. El prototipo tiene un campo propio `frequency` con el
// enum `every | every-2 | every-3 | weekly | biweekly | monthly`
// (`public/plataforma/js/admin.js:390`). **Ese campo no existe en este producto**: ni el
// contrato de la API (`PagedSurveyDto`) ni el dominio del SurveyService tienen nada llamado
// Frequency/Recurrence — comprobado sobre la totalidad del `.cs` del servicio.
//
// Lo que sí existe, se configura en el builder y el backend persiste y devuelve es `activeDays`:
// los días de la semana en que la encuesta está activa (0=lunes … 6=domingo, ver `dayIndex.ts`).
// La columna deriva de ahí. La alternativa era pintar el literal `'Por reunión'` que
// `adaptSurvey` fabricaba para todas las filas por igual, y eso es dato inventado.
//
// Sin `activeDays` la celda dice "—", no un valor por omisión: una encuesta sin días
// configurados es un estado real, no un hueco que haya que rellenar.
// ─────────────────────────────────────────────────────────────────────────────
import { DAY_FULL_KEYS } from '@/views/AdminView/SurveyBuilderView/data';

/** Clave estable de frecuencia. Es también el valor con el que filtra la columna. */
export type FrequencyKey = 'daily' | 'workdays' | 'weekly' | 'custom' | 'none';

const WORKDAYS = [0, 1, 2, 3, 4];

/** Deriva la frecuencia de los días activos. Nunca inventa: sin días, `none`. */
export function frequencyKey(activeDays?: readonly number[]): FrequencyKey {
  if (!activeDays || activeDays.length === 0) return 'none';
  if (activeDays.length === 7) return 'daily';
  if (activeDays.length === 1) return 'weekly';
  const sorted = [...activeDays].sort((a, b) => a - b);
  if (sorted.length === WORKDAYS.length && sorted.every((day, i) => day === WORKDAYS[i])) {
    return 'workdays';
  }
  return 'custom';
}

const LABEL_KEY: Record<FrequencyKey, string> = {
  daily:    'admin.freqDaily',
  workdays: 'admin.freqWorkdays',
  weekly:   'admin.freqWeekly',
  custom:   'admin.freqCustom',
  none:     'admin.freqNone',
};

/** Clave i18n de una frecuencia, para las opciones del filtro. */
export function frequencyLabelKey(key: FrequencyKey): string {
  return LABEL_KEY[key];
}

/**
 * Etiqueta que se pinta en la celda. `weekly` nombra el día ("Semanal · Martes") y `custom`
 * dice cuántos días son; el resto es una cadena fija.
 */
export function frequencyLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  activeDays?: readonly number[],
): string {
  const key = frequencyKey(activeDays);
  if (key === 'weekly' && activeDays?.length === 1) {
    return t('admin.freqWeekly', { day: t(DAY_FULL_KEYS[activeDays[0]] ?? '') });
  }
  if (key === 'custom') {
    return t('admin.freqCustom', { count: activeDays?.length ?? 0 });
  }
  return t(LABEL_KEY[key]);
}

/** Todas las claves de frecuencia, en el orden en que se listan en el filtro. */
export const FREQUENCY_KEYS: FrequencyKey[] = ['daily', 'workdays', 'weekly', 'custom', 'none'];
